import React from 'react'
import Immutable from 'seamless-immutable'
import {find, get, isEmpty, isNil} from 'lodash'
import {getSelectedCompanyId, getSelectedContactId} from 'selectors/contacts'
import {ActivityBaseType} from 'helpers/activities'
import FinancialString from 'containers/financialString'
import {features, templateTypes, USER_LEVEL} from 'helpers/constants'
import {PACKAGES_ORDER} from 'helpers/plans'
import {isPricingGroupsEnabled} from 'helpers/pricingGroups'
import {
  categoriseFields,
  CompanyField,
  ContactField,
  getDetailFieldsOnly,
  removeField,
} from 'helpers/fields'

export const CONTACT_COMPANY_SELECTOR_PAGE_SIZE = 100

export const TYPE = {
  general: 0,
  main: 1,
  dormant: 2,
  accounts: 3,
}

export const FIELD_SELECT_MODE = {
  CONTACT: 'CONTACT',
  COMPANY: 'COMPANY',
  INDIVIDUAL: 'INDIVIDUAL',
}

export const isGeneralContact = (contactType) => {
  return contactType === TYPE.general
}

export const isMainContact = (contactType) => {
  return contactType === TYPE.main
}

export const isDormantContact = (contactType) => {
  return contactType === TYPE.dormant
}

export const isDormantContactOrCompany = (contact, company) => {
  const contactType = get(contact, 'type', null)
  const isCompanyDormant = get(company, 'dormant', false)
  return isDormantContact(contactType) || isCompanyDormant
}

export const filterDormantContacts = (contacts, includeDormants) => {
  return includeDormants
      ? contacts
      : contacts.filter(contact => !isDormantContact(contact.type))
}

export const filterDormantAndNotSelectedContacts = (contacts, selectedId) => {
  return contacts.filter(contact => contact.key === selectedId || !isDormantContact(contact.type))
}

export const isAccountsContact = (contactType) => {
  return contactType === TYPE.accounts
}

export const getMainContact = (contacts) => {
  return contacts.find(contact => isMainContact(contact.type.id || contact.type))
}

export const REFERRAL_TYPE = {
  percentage: 1,
  value: 2,
}

export const COMPANY_TYPE = {
  individual: -1, // None (this is an individual)
  general: 0, // General (this is a company contact)
  home: 1, // Home (this is a home contact)
}

export const FIXED_GROUP = {
  customer: -1,
  supplier: -2,
  prospect: -3,
  suspect: -4,
  approvedSupplier: -5,
  client: -6,
  advocate: -7,
}

export const SUBSCRIPTION_TYPE = {
  userOptOut: -2,     // manually opted out by user
  systemOptOut: -1,   // automatically opted out by system
  recipientOptOut: 0, // recipient opted out themselves
  userOptIn: 1,           // opted in (default)
  recipientOptIn: 2,  // recipient opted in
}

export const LOCATION_TYPE = {
  general: 0,
  main: -1,
  registered: -2,
  accounts: -3,
  delivery: -4,
}

export const HISTORY_TYPE = {
  marketing: 0,
  activity: 1,
  quoted: 2,
  accepted: 3,
  confirmed: 4,
}

export const getSubscriptionTypeLabel = (type) => {
  switch (type) {
    case SUBSCRIPTION_TYPE.userOptOut:
      return 'Manually Opted Out'
    case SUBSCRIPTION_TYPE.systemOptOut:
      return 'Automatically Opted Out'
    case SUBSCRIPTION_TYPE.recipientOptOut:
      return 'Recipient Opted Out'
    case SUBSCRIPTION_TYPE.userOptIn:
      return 'Manually Opted In'
    case SUBSCRIPTION_TYPE.recipientOptIn:
      return 'Recipient Opted In'
  }
}

export const getContactColorForType = (type) => {
  const themeVars = typeof window !== 'undefined' ? {
    general: getComputedStyle(document.documentElement).getPropertyValue('--cg-color-contact-type-general').trim() || '#3f51b5',
    main: getComputedStyle(document.documentElement).getPropertyValue('--cg-color-contact-type-main').trim() || '#4caf50',
    dormant: getComputedStyle(document.documentElement).getPropertyValue('--cg-color-contact-type-dormant').trim() || '#AAA',
    accounts: getComputedStyle(document.documentElement).getPropertyValue('--cg-color-contact-type-accounts').trim() || '#f44336',
  } : {
    general: '#3f51b5',
    main: '#4caf50',
    dormant: '#AAA',
    accounts: '#f44336',
  }

  switch (type) {
    default:
    case TYPE.general:
      return themeVars.general
    case TYPE.main:
      return themeVars.main
    case TYPE.dormant:
      return themeVars.dormant
    case TYPE.accounts:
      return themeVars.accounts
  }
}

// Returns contact type title (always returns a label)
export const getContactTypeLabel = (type) => {
  switch (type) {
    case TYPE.general:
      return 'General'
    case TYPE.main:
      return 'Main'
    case TYPE.dormant:
      return 'Dormant'
    case TYPE.accounts:
      return 'Accounts'
    default:
      return 'General'
  }
}

// Returns contact type title for entity (blank for General)
export const getContactTypeTitleForType = (type) => {
  let contactTypeTitle
  switch (type) {
    case TYPE.general:
      contactTypeTitle = ''
      break
    case TYPE.main:
      contactTypeTitle = 'Main'
      break
    case TYPE.dormant:
      contactTypeTitle = 'Dormant'
      break
    case TYPE.accounts:
      contactTypeTitle = 'Accounts'
      break
    default:
      contactTypeTitle = ''
      break
  }
  return contactTypeTitle
}

export const getLetter = (title) => {
  let ret = title.charAt(0).toUpperCase()
  const code = ret.charCodeAt(0)
  if (code < 65 || code > 90) ret = '#'
  return ret
}

export const getLetterForContact = (entity) => {
  let letter
  if (entity.forename === '' && entity.surname === '') {
    letter = '#'
  } else if (entity.forename !== '' && entity.surname === '') {
    letter = entity.forename
  } else if (entity.forename === '' && entity.surname !== '') {
    letter = entity.surname
  } else {
    letter = entity.forename
  }
  let ret = letter.charAt(0).toUpperCase()
  const code = ret.charCodeAt(0)
  if (code < 65 || code > 90) ret = '#'
  return ret
}

export const getLetterForCompany = (entity) => {
  let letter
  if (entity.name === '') {
    letter = '#'
  } else {
    letter = entity.name
  }
  let ret = letter.charAt(0).toUpperCase()
  const code = ret.charCodeAt(0)
  if (code < 65 || code > 90) ret = '#'
  return ret
}

// Creates an alphabetically sorted array of letters to be used in indexed entity lists
// for contacts and companies based on the mode selected
export const getSectionTitlesForMode = (entities, mode) => {
  let entitySectionTitles = []
  // Create the title index for each entity
  entities.forEach((entity) => {
    let letter
    if (mode === 'CONTACTS') {
      letter = getLetterForContact(entity)
    } else {
      letter = getLetterForCompany(entity)
    }
    if (entitySectionTitles.indexOf(letter) === -1) entitySectionTitles.push(letter)
  })

  // // Sort the index titles alphabetically
  entitySectionTitles = entitySectionTitles.sort(function (a, b) {
    return a.localeCompare(b)
  })

  return entitySectionTitles
}

export const sortContacts = (entities) => {
  let contacts = []
  contacts = contacts.concat(Immutable(entities))
  contacts.sort((a, b) => {
    return getContactName(a).localeCompare(getContactName(b))
  })
  return contacts
}

export const sortCompanies = (entities) => {
  let companies = []
  companies = companies.concat(Immutable(entities))
  companies.sort((a, b) => {
    return a.company.name.localeCompare(b.company.name)
  })
  return companies
}

export const getContactProfileData = (contact, company) => {
  return {
    contactId: contact.id,
    companyId: contact.companyId,
    companyType: contact.companyType,
    companyName: company.name,
    pinned: contact.pinned,
    type: contact.type,
    forename: contact.forename,
    surname: contact.surname,
  }
}

export const getCompanyProfileData = (company) => {
  return {
    companyId: company.id,
    companyName: company.name,
    pinned: company.pinned,
  }
}

export const isIndividual = (contact) => {
  const companyType = get(contact, 'companyType.id', COMPANY_TYPE.general)
  return companyType === COMPANY_TYPE.individual
}

export const getContactLabel = (contact, showCompanyName) => {
  if (isNil(contact))
    return null

  const companyName = get(contact, 'company.name', null)
  const companyType = get(contact, 'company.type.id', COMPANY_TYPE.individual)
  const forename = get(contact, 'forename', null)
  const surname = get(contact, 'surname', null)
  const email = get(contact, 'email', null)

  if (showCompanyName) {
    if (companyName && companyType !== COMPANY_TYPE.individual)
      return `${forename} ${surname} (${companyName})`

    if (!isEmpty(email))
      return `${forename} ${surname} (${email})`
  }
  return `${forename} ${surname}`
}

export const getDormantLabel = (contact) => {
  const type = get(contact, 'type', TYPE.general)
  return type === TYPE.dormant ? '[DORMANT]' : null
}

export const getContactName = (contact) => {
  if (isNil(contact))
    return ''

  const forename = get(contact, 'forename', null)
  const surname = get(contact, 'surname', null)

  if (isEmpty(forename) && isEmpty(surname))
    return ''

  const contactName = [
    forename,
    surname,
  ]

  const type = get(contact, 'type', TYPE.general)
  if (isDormantContact(type))
    contactName.push(getDormantLabel(contact))

  return contactName
      .filter(value => !isEmpty(value))
      .join(' ')
}

export const getContactFullName = (contact) => {
  if (isNil(contact))
    return ''

  const forename = get(contact, 'forename', null)
  const surname = get(contact, 'surname', null)

  if (isEmpty(forename) && isEmpty(surname))
    return ''

  const contactName = [
    forename,
    surname,
  ]

  return contactName
      .filter(value => !isEmpty(value))
      .join(' ')
}

export const getCompanyName = (company) => {
  return get(company, 'name', '')
}

export const validateString = (value) => {
  if (value === undefined || value === null) {
    return ''
  } else {
    return value
  }
}

export const validateNumber = (value) => {
  if (value === undefined || value === null) {
    return 0
  } else {
    return value
  }
}

export const getMultiListValues = () => {
  return []
}

export const getContactTypes = () => {
  return Object.values(TYPE).map(type => ({
    id: type,
    value: type,
    label: getContactTypeLabel(type),
  }))
}

export const getFixedCompanyGroups = () => {
  return [
    {id: FIXED_GROUP.customer, name: 'Customer'},
    {id: FIXED_GROUP.supplier, name: 'Supplier'},
    {id: FIXED_GROUP.prospect, name: 'Prospect'},
    {id: FIXED_GROUP.suspect, name: 'Suspect'},
    {id: FIXED_GROUP.approvedSupplier, name: 'Approved Supplier'},
    {id: FIXED_GROUP.client, name: 'Client'},
    {id: FIXED_GROUP.advocate, name: 'Advocate'},
  ]
}

export const getCompanyGroupIds = (company) => {
  return get(company, 'groupIds', [])
}

export const companyHasFixedGroup = (company, fixedGroup) => {
  const groups = getCompanyGroupIds(company)
  return groups.includes(fixedGroup)
}

export const companyIsCustomer = (company) => {
  return companyHasFixedGroup(company, FIXED_GROUP.customer)
}

export const companyIsSupplier = (company) => {
  return companyHasFixedGroup(company, FIXED_GROUP.supplier)
}

export const getCampaign = (campaignId, campaigns) => {
  if (isNil(campaignId) || (isNil(campaigns) || campaigns.length === 0)) {
    return {name: '<None>'}
  } else {
    return find(campaigns, {id: campaignId})
  }
}

export const getAddress = (location) => {
  if (isNil(location)) return ''
  const address = []
  if (!isEmpty(location.address1)) address.push(location.address1)
  if (!isEmpty(location.address2)) address.push(location.address2)
  if (!isEmpty(location.address3)) address.push(location.address3)
  if (!isEmpty(location.city)) address.push(location.city)
  if (!isEmpty(location.county)) address.push(location.county)
  if (!isEmpty(location.postCode)) address.push(parsePostCode(location.postCode))
  if (!isEmpty(location.country)) address.push(location.country)
  return address.join(', ')
}

export const getFullDetails = (location, companyName, contactName) => {
  const addressName = getAddress(location)
  const details = []
  if (!isEmpty(companyName)) details.push(companyName)
  if (!isEmpty(contactName)) details.push(contactName)
  if (!isEmpty(addressName)) details.push(addressName)
  return details.join(', ')
}

export const getFullAddress = (location) => {
  const address1 = get(location, 'address1', null)
  const address2 = get(location, 'address2', null)
  const address3 = get(location, 'address3', null)
  const addressFields = [address1, address2, address3]
      .filter(addressField => addressField !== null && addressField.length !== 0)
  return isEmpty(addressFields)
      ? ''
      : addressFields.join('\n')
}

export const removeFieldsForHomeCompany = (fields, companyId, homeCompanyId) => {
  return companyId === homeCompanyId
      ? fields
          .slice()
          .filter(field => field.key !== ContactField.ACCOUNT_MANAGER)
          .filter(field => field.key !== ContactField.SOURCE)
      : fields
}

export const removeFieldsForPricingGroup = (fields, isPricingGroupsEnabled) => {
  return isPricingGroupsEnabled
      ? fields
      : removeField(fields, CompanyField.PRICING_GROUP)
}

export const hasContactDetails = contact => {
  const phone = get(contact, 'phone', null)
  const mobile = get(contact, 'mobile', null)
  const email = get(contact, 'email', null)

  return !isNil(contact) && (
      !isEmpty(phone) ||
      !isEmpty(mobile) ||
      !isEmpty(email)
  )
}


export const nullCompany = {
  id: -1,
  name: null,
}

export const nullContact = {
  id: -1,
  urlFacebook: null,
  extension: null,
  urlYouTube: null,
  notes: null,
  urlLinkedIn: null,
  emailStatusModifiedDate: null,
  jobTitle: null,
  creatorId: null,
  modifierId: 1,
  type: 1,
  emailSubscribed: false,
  forename: null,
  surname: null,
  locationId: null,
  modifiedBy: null,
  company: {
    name: null,
    id: -1,
  },
  departments: null,
  accountManagerId: null,
  email: null,
  currencySaleRate: null,
  companyType: 1,
  initials: null,
  campaignId: null,
  homePhone: null,
  urlTwitter: null,
  mobile: null,
  emailStatusModifierId: null,
  fullName: null,
  initialCampaignId: null,
  companyId: 1,
  emailStatus: 1,
  createdDate: null,
  phone: null,
  sourceDesc: null,
  createdBy: null,
  modifiedDate: null,
  salutation: null,
}

export const hasContactMasterLicence = (contactId, contactLicences) => {
  const contactLicence = contactLicences.find(contactLicence => contactLicence.id === contactId)
  return isNil(contactLicence)
      ? false
      : get(contactLicence, 'hasMasterLicence', false)
}

export const updateParamsWithSelectedCompanyContact = (state, params, showAllContacts = false) => {
  const updatedParams = {...params}
  const selectedCompanyId = getSelectedCompanyId(state)
  const selectedContactId = getSelectedContactId(state)

  if (selectedCompanyId !== null)
    updatedParams['companyId'] = selectedCompanyId

  if (selectedContactId !== null && !showAllContacts)
    updatedParams['contactId'] = selectedContactId

  return updatedParams
}

export const getHistoryStatKey = (fixedType) => {
  switch (fixedType) {
    case ActivityBaseType.call:
      return 'totalCalls'
    case ActivityBaseType.meeting:
      return 'totalMeetings'
    case ActivityBaseType.toDo:
      return 'totalToDos'
    case ActivityBaseType.note:
      return 'totalNotes'
    case ActivityBaseType.task:
      return 'totalTasks'
    case ActivityBaseType.email:
      return 'totalEmails'
    case ActivityBaseType.quoteFollowUp:
      return 'totalQuoteFollowUps'
    default:
      return 'totalCalls'
  }
}

export const getHistoryStatValue = (contact, historyStatKey) => {
  const historyStatValue = get(contact, `historystat.${historyStatKey}`, null)
  return isNil(historyStatValue)
      ? 0
      : historyStatValue
}

export const mapHomeContactsToOptions = (contacts) => {
  return contacts.map(contact => {
    const contactName = getContactName(contact)
    return {
      id: contact.id,
      key: contact.id,
      value: contact.id,
      label: contactName,
      name: contactName,
    }
  })
}

export const getActivityOwnerField = (homeContacts = [], categoryOrder, order) => {
  return {
    categoryOrder,
    fieldName: 'Activity Owner',
    dataType: 'List',
    values: mapHomeContactsToOptions(homeContacts),
    active: true,
    readOnly: false,
    label: 'Activity Owner',
    type: 'field',
    categoryName: 'Basic',
    mandatory: true,
    formFieldClassName: 'activityOwnerId',
    field: 'activityOwnerId',
    key: 'activityOwnerId',
    options: {},
    user: false,
    order,
    object: 'contact',
    errorText: 'Automatically create a pending call for this owner',
  }
}

export const getContact = (contacts, type) => {
  return contacts.find(contact => contact.type.id === type || contact.type === type)
}

export const parsePostCode = (postCode) => {
  return isNil(postCode)
      ? ''
      : postCode.toUpperCase()
}

export const parseWebsite = (website) => {
  let fullWebsite
  if (website && website.length > 0) {
    fullWebsite = !website.startsWith('http')
        ? 'http://' + website
        : website
  }

  return fullWebsite
}

export const getInitials = (contact) => {
  return [
    get(contact, 'forename', null),
    get(contact, 'surname', null)]
      .filter(value => !isEmpty(value))
      .map(value => value[0])
      .join('')
}

export const getInitialsFromName = (name) => {
  return isEmpty(name) ? '' : name.split(' ')
      .filter(value => !isEmpty(value))
      .map(value => value[0])
      .join('')
}

export const shouldDormantCompany = (dormantingContact, companyContacts) => {
  return isEmpty(getOtherContacts(dormantingContact, companyContacts))
}

export const getOtherContacts = (contact, contacts) => {
  return contacts
      ? contacts.filter(c => c.id !== contact.id)
      : []
}

export const getCompaniesFromContacts = (contacts) => {
  const companiesMap = {}
  contacts.forEach(contact => {
    companiesMap[get(contact, 'companyId')] = contact.company
  })

  return Object.values(companiesMap)
}

export const getAccountingCompany = (contact) => {
  return get(contact, 'company.accountingcompany', null)
}

export const populateFieldsWithAccountingCompanyFields = (fields) => {
  const updatedFields = {...fields}

  updatedFields.accountingStatus = {
    key: 'accountingStatus',
    label: 'Account Status',
    alias: 'Status',
    size: 120,
    align: 'left',
    data: e => {
      const accountingCompany = getAccountingCompany(e)
      return get(accountingCompany, 'status', '')
    },
    export: e => {
      const accountingCompany = getAccountingCompany(e)
      return get(accountingCompany, 'status', '')
    },
    page: 'accounts',
    visible: false,
  }

  updatedFields.accountingOnHold = {
    key: 'accountingOnHold',
    label: 'Account On Hold',
    alias: 'On Hold',
    size: 120,
    align: 'left',
    data: e => {
      const accountingCompany = getAccountingCompany(e)
      return accountingCompany && accountingCompany.onHold
          ? 'On Hold'
          : 'No'
    },
    export: e => {
      const accountingCompany = getAccountingCompany(e)
      return accountingCompany && accountingCompany.onHold
          ? 'On Hold'
          : 'No'
    },
    page: 'accounts',
    visible: false,
  }

  updatedFields.accountingBalance = {
    key: 'accountingBalance',
    label: 'Account Balance',
    alias: 'Account Balance',
    size: 120,
    align: 'left',
    data: e => {
      const accountingCompany = getAccountingCompany(e)
      return accountingCompany
          ? <FinancialString value={accountingCompany.balance}/>
          : <FinancialString value={0}/>
    },
    export: e => {
      const accountingCompany = getAccountingCompany(e)
      return get(accountingCompany, 'balance', '')
    },
    page: 'accounts',
    visible: false,
  }

  updatedFields.accountingCreditLimit = {
    key: 'accountingCreditLimit',
    label: 'Account Credit Limit',
    alias: 'Account Credit Limit',
    size: 200,
    align: 'left',
    data: e => {
      const accountingCompany = getAccountingCompany(e)
      return accountingCompany
          ? <FinancialString value={accountingCompany.creditLimit}/>
          : <FinancialString value={0}/>
    },
    export: e => {
      const accountingCompany = getAccountingCompany(e)
      return get(accountingCompany, 'creditLimit', '')
    },
    page: 'accounts',
    visible: false,
  }

  updatedFields.accountingAvailableCredit = {
    key: 'accountingAvailableCredit',
    label: 'Account Available Credit',
    alias: 'Account Available Credit',
    size: 200,
    align: 'left',
    data: e => {
      const accountingCompany = getAccountingCompany(e)
      return accountingCompany
          ? <FinancialString value={accountingCompany.availableCredit}/>
          : <FinancialString value={0}/>
    },
    export: e => {
      const accountingCompany = getAccountingCompany(e)
      return get(accountingCompany, 'availableCredit', '')
    },
    page: 'accounts',
    visible: false,
  }

  return updatedFields
}

export const getContactOptions = (contacts) => {
  return contacts.map(contact => ({
    value: contact.id,
    label: getContactName(contact),
    contact,
  }))
}

export const isMasterOrLoginContact = (selectedContact, loginUser, userLinkedToSelectedContact) => {
  const selectedContactUserLevel = get(userLinkedToSelectedContact, 'level', USER_LEVEL.USER)
  return selectedContact.id === loginUser.contactId
      || selectedContactUserLevel === USER_LEVEL.MASTER
}

export const getUserLinkedToContact = (contact, allUsers) => {
  return contact && allUsers.find(user => user.contactId === contact.id)
}

export const getOrderedLicences = (licences) => {
  const licenceMap = new Map(licences.map(licence => [licence.id, licence]))
  const sortedIds = licenceOrder.filter(id => licenceMap.has(id))
  const remainingLicences = licences.filter(licence => !sortedIds.includes(licence.id))
  const sortedLicences = sortedIds.map(id => licenceMap.get(id))
  return sortedLicences.concat(remainingLicences)
}

export const getOrderedPackages = (packages) => {
  if (packages.length === 1) return packages
  const packagesMap = new Map(packages.map(packageValue => [packageValue.partCode, packageValue]))
  const sortedIds = PACKAGES_ORDER.filter(partCode => packagesMap.has(partCode))
  return sortedIds.map(partCode => packagesMap.get(partCode))
}

export const getDefaultOrderedPackageAndLicences = (userPackages) => {
  for (const partCode of PACKAGES_ORDER) {
    const userPackage = userPackages.find(userPackage => userPackage.partCode === partCode)


    if (userPackage) {
      const isPackageAvailable = userPackage.used < userPackage.qty

      if (isPackageAvailable) {
        const filteredLicences = userPackage.licences.filter(licence => {
          return licenceOrder.includes(licence.licenceId)
        })
        return {
          userPackages: [{
            id: userPackage.id,
            partCode: userPackage.partCode,
            licences: filteredLicences.map(filteredLicence => filteredLicence.licenceId),
          }],
          licences: filteredLicences.map(filteredLicence => filteredLicence.licenceId),
        }
      }
    }
  }
  return {
    licences: [],
    userPackages: [],
  }
}

export const licenceOrder = [
  features.CONTACTS_LICENCE,
  features.ACTIVITY_LICENCE,
  features.SALES_LICENCE,
  features.SALES_PLUS_LICENCE,
  features.PRICE_LISTS_LICENCE,
  features.QUOTING_LICENCE,
  features.PURCHASING_LICENCE,
  features.PRODUCTION_LICENCE,
  features.DELIVERIES_LICENCE,
  features.DELIVERY_SCHEDULING,
  features.INVOICING_LICENCE,
  features.SAGE_50,
  features.POD_LICENCE,
]

export const getFieldsForSelect = (user, fieldSelectMode, contactFields, companyFields, individualFields) => {
  let fields = getFieldsForMode(fieldSelectMode, contactFields, companyFields, individualFields)
  fields = removeFieldsForPricingGroup(fields, isPricingGroupsEnabled(user))
  fields = getDetailFieldsOnly(fields, true)
  fields.sort(sortFieldsByLabel)
  return categoriseFields(fields)
}

export const getFieldsForMode = (fieldSelectMode, contactFields, companyFields, individualFields) => {
  switch (fieldSelectMode) {
    case FIELD_SELECT_MODE.CONTACT:
      return contactFields
    case FIELD_SELECT_MODE.COMPANY:
      return companyFields
    case FIELD_SELECT_MODE.INDIVIDUAL:
      return individualFields
    default:
      return []
  }
}

const sortFieldsByLabel = (a, b) => {
  if (a.label > b.label) return 1
  if (a.label < b.label) return -1
  return 0
}

export const isSearchingAllFields = (searchField) => {
  return isEmpty(searchField)
}

export const getSearchField = (search, searchField) => {
  const isSearching = !isEmpty(search)
  return isSearching
      ? searchField
      : undefined
}

export const findContactIdForOpenCompany = (openCompanies, companyId) => {
  const company = openCompanies.find(company => company.id === companyId)
  return get(company, 'contact.id')
}

export const findMostRecentContactForCompany = (recentContacts, companyId) => {
  const contact = recentContacts.find(recentContact => recentContact.companyId === companyId)
  return get(contact, 'id')
}

export const getContactFields = () => {
  return {
    default: true,
    company: {
      default: true,
      companyLogoImage: true,
      contacts: {
        default: true,
        company: {
          id: true,
          name: true,
        },
      },
      locations: true,
      accountingcompany: true,
      referralcompany: true,
      userfields: true,
      groups: true,
      products: true,
      createdBy: true,
      modifiedBy: true,
    },
    contactmethods: true,
    userfields: true,
    accountmanager: true,
    campaign: true,
    latestOrder: true,
    latestPurchaseOrder: true,
  }
}

export const getCompanyFields = () => {
  return {
    default: true,
    companyLogoImage: true,
    contacts: {
      default: true,
      company: {
        id: true,
        name: true,
      },
    },
    locations: true,
    accountingcompany: true,
    referralcompany: true,
    userfields: true,
    groups: true,
    products: true,
    historystat: getLegacyHistoryStatFields(),
    latestOrder: true,
    latestPurchaseOrder: true,
    createdBy: true,
    modifiedBy: true,
  }
}

const getLegacyHistoryStatFields = () => {
  return {
    totalSpend: true,
    lastInvoice: true,
    rollingYearSpend: true,
    previousRollingYearSpend: true,
    totalRejected: true,
    totalRejectedQuotes: true,
    rejectReason1: true,
    rejectReason2: true,
    rejectReason3: true,
    rejectPercent1: true,
    rejectPercent2: true,
    rejectPercent3: true,
  }
}

export const isTermsField = (field) => {
  const category = get(field, 'categoryName', '')
  return category === 'Terms'
}

export const filterTemplatesByType = (templates, typeId) => {
  if (isEmpty(templates)) return []
  return templates.filter(template => get(template, 'type.id') === typeId)
}

export const getFilteredTemplates = (field, templates) => {
  const fieldKey = get(field, 'key')

  if (fieldKey === CompanyField.PAYMENT_TERMS || fieldKey === CompanyField.QUOTE_TERMS) {
    return filterTemplatesByType(templates, templateTypes.TERMS)
  } else if (fieldKey === CompanyField.PURCHASE_TERMS) {
    return filterTemplatesByType(templates, templateTypes.PURCHASING_TERMS)
  } else {
    return []
  }
}

export default {
  TYPE,
  COMPANY_TYPE,
  SUBSCRIPTION_TYPE,
  REFERRAL_TYPE,
  LOCATION_TYPE,
  HISTORY_TYPE,
  getSubscriptionTypeLabel,
  getContactColorForType,
  getContactTypeLabel,
  getContactTypeTitleForType,
  getContactTypes,
  getLetter,
  getLetterForContact,
  getLetterForCompany,
  getSectionTitlesForMode,
  sortContacts,
  sortCompanies,
  getContactProfileData,
  getCompanyProfileData,
  getContactLabel,
  getContactName,
  getCompanyName,
  validateString,
  validateNumber,
  getMultiListValues,
  getFixedCompanyGroups,
  getCampaign,
  getAddress,
}
